import { useState, type FormEvent, type ReactNode } from "react";
import { Moai } from "./Moai.js";
import type { SkinId } from "./skins.js";
import { Barre, Bulle, Ecran, Etape, Feuille, Parole, Retour } from "./ui.js";

/**
 * Les écrans du compte : créer, se connecter, remplacer un mot de passe
 * oublié avec la clé de secours, montrer cette clé, supprimer le compte.
 *
 * Même forme que « Nouvelle tribu » : Hoa pose la question en tête, les
 * champs en dessous, le bouton d'action en bas de l'écran.
 */

export type Envoi = { en: boolean; erreur: string | null };

function Formulaire(props: {
  skin: SkinId;
  titre: string;
  question: ReactNode;
  action: string;
  envoi: Envoi;
  pret: boolean;
  onEnvoyer: () => void;
  onRetour: () => void;
  children: ReactNode;
  apres?: ReactNode;
}): JSX.Element {
  const envoyer = (e: FormEvent): void => {
    e.preventDefault();
    if (props.pret && !props.envoi.en) props.onEnvoyer();
  };
  return (
    <Ecran skin={props.skin} label={props.titre}>
      <Barre>
        <button type="button" className="hoa-rond" aria-label="Retour" onClick={props.onRetour}>
          <Retour />
        </button>
        <span className="titre">{props.titre}</span>
      </Barre>
      <form className="hoa-compte" onSubmit={envoyer} noValidate>
        <div className="hoa-etape courante hoa-creer">
          <div className="hoa-noeud" aria-hidden="true">
            <Moai expression="sourire" />
            <u />
          </div>
          <div className="hoa-corps hoa-form">
            <Bulle>{props.question}</Bulle>
            {props.children}
            {props.envoi.erreur ? (
              <div className="hoa-erreur" role="alert">
                {props.envoi.erreur}
              </div>
            ) : null}
          </div>
        </div>
        <div className="hoa-pile" style={{ marginTop: "auto" }}>
          <button type="submit" className="hoa-go" disabled={!props.pret || props.envoi.en}>
            {props.envoi.en ? "Un instant…" : props.action}
          </button>
          {props.apres}
        </div>
      </form>
    </Ecran>
  );
}

function Champ(props: {
  id: string;
  libelle: string;
  type?: string;
  valeur: string;
  onChange: (v: string) => void;
  autoComplete: string;
  placeholder?: string;
  note?: string;
  inputMode?: "email" | "text";
}): JSX.Element {
  const [voir, setVoir] = useState(false);
  const secret = props.type === "password";
  return (
    <div>
      <label className="hoa-etiquette" htmlFor={props.id}>
        {props.libelle}
      </label>
      <div className="hoa-champ-bloc">
        <input
          id={props.id}
          className="hoa-champ carre"
          type={secret && !voir ? "password" : secret ? "text" : (props.type ?? "text")}
          value={props.valeur}
          placeholder={props.placeholder}
          autoComplete={props.autoComplete}
          inputMode={props.inputMode}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          onChange={(e) => props.onChange(e.target.value)}
        />
        {secret ? (
          <button type="button" className="hoa-voir" aria-pressed={voir} onClick={() => setVoir((v) => !v)}>
            {voir ? "Cacher" : "Voir"}
          </button>
        ) : null}
      </div>
      {props.note ? <p className="hoa-note petit">{props.note}</p> : null}
    </div>
  );
}

const emailPlausible = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e.trim());
const MIN = 8;

export function CreerCompte(props: {
  skin: SkinId;
  envoi: Envoi;
  onCreer: (e: { email: string; motDePasse: string }) => void;
  onConnexion: () => void;
  onRetour: () => void;
}): JSX.Element {
  const [email, setEmail] = useState("");
  const [mdp, setMdp] = useState("");
  return (
    <Formulaire
      skin={props.skin}
      titre="Créer un compte"
      question={<>Ton e-⁠mail et un mot de passe. Je ne t'écrirai pas : l'e-⁠mail sert seulement à te reconnaître.</>}
      action="Créer mon compte"
      envoi={props.envoi}
      pret={emailPlausible(email) && [...mdp].length >= MIN}
      onEnvoyer={() => props.onCreer({ email: email.trim(), motDePasse: mdp })}
      onRetour={props.onRetour}
      apres={
        <button type="button" className="hoa-lien" onClick={props.onConnexion}>
          J'ai déjà un compte
        </button>
      }
    >
      <Champ id="hoa-email" libelle="E-mail" type="email" inputMode="email" valeur={email} onChange={setEmail} autoComplete="email" placeholder="toi@exemple.fr" />
      <Champ
        id="hoa-mdp"
        libelle="Mot de passe"
        type="password"
        valeur={mdp}
        onChange={setMdp}
        autoComplete="new-password"
        note={`${MIN} caractères au moins. Une petite phrase marche bien.`}
      />
    </Formulaire>
  );
}

export function Connexion(props: {
  skin: SkinId;
  envoi: Envoi;
  onConnecter: (e: { email: string; motDePasse: string }) => void;
  onOubli: () => void;
  onCreer: () => void;
  onRetour: () => void;
}): JSX.Element {
  const [email, setEmail] = useState("");
  const [mdp, setMdp] = useState("");
  return (
    <Formulaire
      skin={props.skin}
      titre="Se connecter"
      question={<>Re-bonjour. Ton e-⁠mail et ton mot de passe, et je te retrouve.</>}
      action="Me connecter"
      envoi={props.envoi}
      pret={emailPlausible(email) && mdp.length > 0}
      onEnvoyer={() => props.onConnecter({ email: email.trim(), motDePasse: mdp })}
      onRetour={props.onRetour}
      apres={
        <div className="hoa-liens">
          <button type="button" className="hoa-lien" onClick={props.onOubli}>
            Mot de passe oublié
          </button>
          <button type="button" className="hoa-lien" onClick={props.onCreer}>
            Créer un compte
          </button>
        </div>
      }
    >
      <Champ id="hoa-email" libelle="E-mail" type="email" inputMode="email" valeur={email} onChange={setEmail} autoComplete="username" placeholder="toi@exemple.fr" />
      <Champ id="hoa-mdp" libelle="Mot de passe" type="password" valeur={mdp} onChange={setMdp} autoComplete="current-password" />
    </Formulaire>
  );
}

export function Secours(props: {
  skin: SkinId;
  envoi: Envoi;
  onRemplacer: (e: { email: string; cleSecours: string; motDePasse: string }) => void;
  onRetour: () => void;
}): JSX.Element {
  const [email, setEmail] = useState("");
  const [cle, setCle] = useState("");
  const [mdp, setMdp] = useState("");
  const cleValide = /^[A-Za-z]\d{6}$/.test(cle.replace(/[\s-]/g, ""));
  return (
    <Formulaire
      skin={props.skin}
      titre="Mot de passe oublié"
      question={<>Ta clé de secours remplace le mot de passe. C'est la lettre et les six chiffres que je t'ai donnés à la création.</>}
      action="Changer le mot de passe"
      envoi={props.envoi}
      pret={emailPlausible(email) && cleValide && [...mdp].length >= MIN}
      onEnvoyer={() => props.onRemplacer({ email: email.trim(), cleSecours: cle, motDePasse: mdp })}
      onRetour={props.onRetour}
    >
      <Champ id="hoa-email" libelle="E-mail" type="email" inputMode="email" valeur={email} onChange={setEmail} autoComplete="username" placeholder="toi@exemple.fr" />
      <Champ id="hoa-cle" libelle="Clé de secours" valeur={cle} onChange={setCle} autoComplete="off" placeholder="R614782" />
      <Champ
        id="hoa-mdp"
        libelle="Nouveau mot de passe"
        type="password"
        valeur={mdp}
        onChange={setMdp}
        autoComplete="new-password"
        note={`${MIN} caractères au moins.`}
      />
    </Formulaire>
  );
}

/** La clé de secours, montrée une seule fois. */
export function CleSecours(props: { skin: SkinId; cle: string; nouvelle: boolean; onSuite: () => void }): JSX.Element {
  const [copie, setCopie] = useState(false);
  const lisible = `${props.cle.slice(0, 1)} ${props.cle.slice(1, 4)} ${props.cle.slice(4)}`;
  return (
    <Ecran skin={props.skin} label="Clé de secours">
      <Barre>
        <span className="titre">{props.nouvelle ? "Mot de passe changé" : "Compte créé"}</span>
      </Barre>
      <div className="hoa-frise">
        <Etape etat="courante" expression="pouce" dernier>
          <Bulle>
            {props.nouvelle ? "C'est fait. Ta clé de secours a changé, voici la nouvelle :" : "Ton compte est prêt. Voici ta clé de secours :"}
            <big className="xl hoa-cle">{lisible}</big>
            Note-la quelque part. Si tu oublies ton mot de passe, elle seule permet d'en choisir un autre. Je ne la montrerai plus.
          </Bulle>
        </Etape>
      </div>
      <div className="hoa-pile" style={{ marginTop: "auto" }}>
        <button type="button" className="hoa-go" onClick={props.onSuite}>
          Je l'ai notée
        </button>
        <button
          type="button"
          className="hoa-go2"
          onClick={() => {
            void navigator.clipboard?.writeText(props.cle).then(
              () => setCopie(true),
              () => setCopie(false),
            );
          }}
        >
          {copie ? "Copiée" : "Copier la clé"}
        </button>
      </div>
    </Ecran>
  );
}

/** Supprimer le compte : la seule confirmation de l'application (wireframe). */
export function FeuilleSupprimer(props: {
  envoi: Envoi;
  onSupprimer: (motDePasse: string) => void;
  onFermer: () => void;
}): JSX.Element {
  const [mdp, setMdp] = useState("");
  return (
    <Feuille label="Supprimer mon compte" onFermer={props.onFermer}>
      <Parole expression="mefiant">Ton e-⁠mail, ton mot de passe et ta clé de secours seront effacés. Ce n'est pas réversible.</Parole>
      <form
        className="hoa-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (mdp && !props.envoi.en) props.onSupprimer(mdp);
        }}
      >
        <Champ id="hoa-mdp-suppr" libelle="Ton mot de passe, pour confirmer" type="password" valeur={mdp} onChange={setMdp} autoComplete="current-password" />
        {props.envoi.erreur ? (
          <div className="hoa-erreur" role="alert">
            {props.envoi.erreur}
          </div>
        ) : null}
        <button type="submit" className="hoa-go" disabled={!mdp || props.envoi.en}>
          {props.envoi.en ? "Un instant…" : "Supprimer mon compte"}
        </button>
        <button type="button" className="hoa-lien" onClick={props.onFermer}>
          Annuler
        </button>
      </form>
    </Feuille>
  );
}

/** Effacer les données de cet appareil : une confirmation, puis Hoa repart de zéro. */
export function FeuilleEffacerAppareil(props: { avecCompte: boolean; enTribu: boolean; onEffacer: () => void; onFermer: () => void }): JSX.Element {
  const [en, setEn] = useState(false);
  return (
    <Feuille label="Effacer les données de cet appareil" onFermer={props.onFermer}>
      <Parole expression="mefiant">
        {props.avecCompte
          ? "J'oublie tout ce que ce téléphone garde : ta connexion, tes soirées, ta photo, ton thème, tes réglages. Ton compte n'est pas supprimé : tu t'y reconnectes avec ton e-mail et ton mot de passe."
          : "J'oublie tout ce que ce téléphone garde : tes soirées, tes points, ta photo, ton thème, tes réglages. Sans compte, rien ne pourra être retrouvé."}
        {props.enTribu ? " Tu quittes aussi ta tribu." : ""}
      </Parole>
      <button
        type="button"
        className="hoa-go"
        disabled={en}
        onClick={() => {
          setEn(true);
          props.onEffacer();
        }}
      >
        {en ? "Un instant…" : "Tout effacer"}
      </button>
      <button type="button" className="hoa-lien" onClick={props.onFermer}>
        Annuler
      </button>
    </Feuille>
  );
}
